import {Component, OnInit, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {CommonModule} from "@angular/common";
import {forkJoin, of} from "rxjs";
import {StockItem} from "../../models/stock";
import {DEFAULT_TEMPERATURE_ZONE, TemperatureZone} from "../../models/temperature-zone";
import {StockService} from "../../services/stock/stock.service";
import {UiButtonComponent, UiInputComponent, UiModalComponent, UiRangeComponent} from "../../shared/ui";
import {AppLogger, AppLoggerService} from "../../core/logging";
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {faSnowflake, faTemperatureLow, faHouse} from "@fortawesome/free-solid-svg-icons";

@Component({
  selector: 'app-stock-item',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    UiModalComponent,
    UiRangeComponent,
    UiButtonComponent,
    UiInputComponent,
    FontAwesomeModule
  ],
  templateUrl: './stock-item.component.html',
  styleUrl: './stock-item.component.scss'
})
export class StockItemComponent implements OnInit {

  readonly log: AppLogger;

  @ViewChild('modal') modal!: UiModalComponent;

  stockItem: StockItem;
  quantityLeft: number = 0;
  quantityFull: number = 0;
  quantityFullOriginal: number = 0;
  quantityType: 'fixed' | 'variable' = 'fixed';
  sliderValue: number = 0;

  // Compact zone selector: icon + short label so buttons stay on one line.
  readonly zoneButtons: { value: TemperatureZone; label: string; icon: IconDefinition }[] = [
    {value: 'frozen', label: 'Freezer', icon: faSnowflake},
    {value: 'refrigerated', label: 'Fridge', icon: faTemperatureLow},
    {value: 'ambient', label: 'Room', icon: faHouse},
  ];
  selectedZone: TemperatureZone = DEFAULT_TEMPERATURE_ZONE;

  private onCloseCallback: ((result: any) => void) | null = null;

  constructor(
    private stockService: StockService,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(StockItemComponent);
  }

  ngOnInit(): void {

    if (this.stockItem) {
      this.quantityLeft = this.parseQuantity(this.stockItem.quantity_left);
      this.quantityFull = this.parseQuantity(this.stockItem.quantity_full);
      this.updateSliderFromQuantity();
    }
  }

  private parseQuantity(value: any): number {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    const raw = (value as string).trim().replace(',', '.');
    const val = parseFloat(raw);
    return isNaN(val) ? 0 : val;
  }

  isWeightOrVolume(): boolean {
    const type = (this.stockItem?.unit_type || '').toLowerCase();
    return type === 'weight' || type === 'volume';
  }

  isPiece(): boolean {
    const type = (this.stockItem?.unit_type || '').toLowerCase();
    return type === 'piece';
  }

  getUsagePercentage(): number {
    if (!this.quantityFull || this.quantityFull <= 0) return 0;
    let percentage = (this.quantityLeft / this.quantityFull) * 100;
    return Math.max(0, Math.min(100, percentage));
  }

  updateSliderFromQuantity(): void {
    this.sliderValue = this.getUsagePercentage();
  }

  onSliderChange(value: string | number): void {
    this.sliderValue = typeof value === 'string' ? parseFloat(value) : value;
    this.quantityLeft = (this.sliderValue / 100) * this.quantityFull;
  }

  onQuantityLeftChange(): void {
    if (this.quantityLeft > this.quantityFull) {
      this.quantityLeft = this.quantityFull;
    }
    this.updateSliderFromQuantity();
  }

  onQuantityFullChange(): void {
    if (this.quantityLeft > this.quantityFull) {
      this.quantityLeft = this.quantityFull;
    }
    this.updateSliderFromQuantity();
  }

  setQuickValue(percentage: number): void {
    this.sliderValue = percentage;
    this.quantityLeft = (percentage / 100) * this.quantityFull;
  }

  incrementPiece(): void {
    if (this.quantityLeft < this.quantityFull) {
      this.quantityLeft++;
    }
  }

  decrementPiece(): void {
    if (this.quantityLeft > 0) {
      this.quantityLeft--;
    }
  }

  save(): void {
    const quantityFullChanged = this.quantityType === 'variable' && this.quantityFull !== this.quantityFullOriginal;
    const zoneChanged = this.selectedZone !== this.stockItem.temperature_zone;

    forkJoin({
      left: this.stockService.updateQuantity(this.stockItem.id, this.quantityLeft),
      full: quantityFullChanged
        ? this.stockService.updateQuantityFull(this.stockItem.id, this.quantityFull)
        : of(null),
      zone: zoneChanged
        ? this.stockService.updateTemperatureZone(this.stockItem.id, this.selectedZone)
        : of(null)
    }).subscribe({
      next: () => {
        this.log.info('Updated stock item', {
          stockItemId: this.stockItem.id,
          quantityLeft: this.quantityLeft,
          quantityFull: quantityFullChanged ? this.quantityFull : undefined,
          temperatureZone: zoneChanged ? this.selectedZone : undefined
        });
        this.stockItem.quantity_left = this.quantityLeft;
        if (quantityFullChanged) {
          this.stockItem.quantity_full = this.quantityFull;
        }
        if (zoneChanged) {
          this.stockItem.temperature_zone = this.selectedZone;
        }
        if (this.onCloseCallback) {
          this.onCloseCallback({
            success: true,
            quantityLeft: this.quantityLeft,
            quantityFull: this.quantityFull,
            stockItem: this.stockItem
          });
        }
        this.modal.close();
      },
      error: (err) => {
        this.log.error('Failed to update stock item', err);
      }
    });
  }

  cancel(): void {
    this.modal.close();
  }

  onModalClosed(): void {
    // Modal was closed without saving
  }

  open(stockItem: StockItem, quantityType: 'fixed' | 'variable' = 'fixed', storeDefaultZone: TemperatureZone | null = null): Promise<any> {
    this.stockItem = stockItem;
    this.quantityType = quantityType;
    this.quantityLeft = this.parseQuantity(stockItem.quantity_left);
    this.quantityFull = this.parseQuantity(stockItem.quantity_full);
    this.quantityFullOriginal = this.quantityFull;
    // Preselect: the item's own zone, else the store default, else room temperature.
    this.selectedZone = stockItem.temperature_zone ?? storeDefaultZone ?? DEFAULT_TEMPERATURE_ZONE;
    this.updateSliderFromQuantity();

    this.modal.open();

    return new Promise((resolve) => {
      this.onCloseCallback = resolve;
    });
  }

  clampNumber(v: string) {
    const n = Number(v);
    if (Number.isNaN(n)) return this.quantityLeft;
    return Math.max(0, Math.min(this.quantityFull, Math.floor(n)));
  }
}
